// @ts-check
// Modo de edição do menu (doc 07, seção 4.8; contrato 08-contrato-navegacao-mobile.md, seção 2): "Meu menu" (ordem e
// ocultos da pessoa) ou "Padrão da escola" (flag WORKSPACE: ordem, seção e ocultos para todos), com subir e descer,
// ocultar, trocar de seção e itens novos (view ou link) em cada seção. Subir e descer em vez de arrastar funciona igual
// no celular, como as ações "Move up" e "Move down" do painel de edição do Twenty.
import { html } from 'htm/preact';
import { signal } from '@preact/signals';
import { useRef, useState } from 'preact/hooks';
import { allNavigationItemsState, hasCurrentPermissionFlag, navigationLayoutState, objectsById, viewsState } from '../../data/metadata-store.js';
import { createNavigationItem, deleteNavigationItem, saveMemberNavigation, saveWorkspaceNavigation } from '../../data/navigation.js';
import {
  createNavigationDraft, moveNavigationItem, moveNavigationItemToSection, serializeNavigationDraft, toggleNavigationItemHidden,
} from '../../lib/navigation-layout.js';
import { canReadObjectRecords } from '../../lib/permissions.js';
import { Icon } from '../../ui/icon.js';
import { Button, LightIconButton } from '../../ui/button.js';
import { Dropdown, DropdownMenuItems, DropdownMenuSearchInput, DropdownMenuSectionLabel, DropdownMenuSeparator, MenuItem } from '../../ui/dropdown.js';
import { Modal } from '../../ui/modal.js';
import { showErrorSnackbar, showSuccessSnackbar } from '../../ui/snackbar.js';
import { translateMetadataLabel, t } from '../../i18n/index.js';

/** @type {import('@preact/signals').Signal<import('../../lib/navigation-layout.js').NavigationDraft | null>} */
export const navigationDraftState = signal(null);
const isSavingState = signal(false);

/** @param {'MEMBER' | 'WORKSPACE'} scope */
export function startNavigationEdit(scope) {
  navigationDraftState.value = createNavigationDraft(scope, allNavigationItemsState.value, navigationLayoutState.value);
}

export function cancelNavigationEdit() {
  navigationDraftState.value = null;
}

async function saveNavigationEdit() {
  const draft = navigationDraftState.value;
  if (!draft) return;
  isSavingState.value = true;
  try {
    if (draft.scope === 'WORKSPACE') await saveWorkspaceNavigation(/** @type {any} */ (serializeNavigationDraft(draft)));
    else await saveMemberNavigation(/** @type {any} */ (serializeNavigationDraft(draft)));
    navigationDraftState.value = null;
    showSuccessSnackbar(t('navigationMobile.editor.saved'));
  } catch (error) {
    showErrorSnackbar(error);
  } finally {
    isSavingState.value = false;
  }
}

async function resetMemberNavigation() {
  isSavingState.value = true;
  try {
    await saveMemberNavigation({ reset: true });
    navigationDraftState.value = null;
    showSuccessSnackbar(t('navigationMobile.editor.resetDone'));
  } catch (error) {
    showErrorSnackbar(error);
  } finally {
    isSavingState.value = false;
  }
}

/** Barra do topo da gaveta no modo de edição: escopo, salvar, cancelar e voltar ao padrão. */
export function NavigationEditBar() {
  const draft = navigationDraftState.value;
  if (!draft) return null;
  const canEditSchool = hasCurrentPermissionFlag('WORKSPACE');
  const isSaving = isSavingState.value;
  return html`<div class="navigation-edit-bar" data-testid="navigation-edit-bar">
    <div class="navigation-edit-bar-title">
      <${Icon} name="IconPaint" size=${16} />
      <span>${t('navigationMobile.editor.title')}</span>
    </div>
    ${canEditSchool
      ? html`<div class="navigation-edit-scope" role="tablist">
          ${/** @type {const} */ (['MEMBER', 'WORKSPACE']).map((scope) => html`<button
            key=${scope}
            type="button"
            role="tab"
            aria-selected=${draft.scope === scope ? 'true' : 'false'}
            class="navigation-edit-scope-button"
            onClick=${() => draft.scope !== scope && startNavigationEdit(scope)}
          >${t(`navigationMobile.editor.scopes.${scope}`)}</button>`)}
        </div>`
      : null}
    <p class="navigation-edit-hint">${t(draft.scope === 'WORKSPACE' ? 'navigationMobile.editor.workspaceHint' : 'navigationMobile.editor.memberHint')}</p>
    <div class="navigation-edit-actions">
      <${Button} title=${t('common.cancel')} variant="secondary" size="small" onClick=${cancelNavigationEdit} disabled=${isSaving} />
      <${Button} title=${t('common.save')} accent="blue" size="small" onClick=${saveNavigationEdit} isLoading=${isSaving} />
    </div>
    ${draft.scope === 'MEMBER'
      ? html`<button type="button" class="navigation-edit-reset" onClick=${resetMemberNavigation} disabled=${isSaving}>
          ${t('navigationMobile.editor.reset')}
        </button>`
      : null}
  </div>`;
}

/**
 * Uma linha do modo de edição: subir, descer, ocultar, seção (no padrão da escola) e remover (itens criados).
 * @param {{ item: import('../../data/metadata-store.js').NavigationEntry, sections: readonly string[], isFirst: boolean, isLast: boolean }} props
 */
function EditableNavigationItem({ item, sections, isFirst, isLast }) {
  const draft = /** @type {import('../../lib/navigation-layout.js').NavigationDraft} */ (navigationDraftState.value);
  const isHidden = draft.hiddenIds.includes(item.id);
  const canDelete = draft.scope === 'WORKSPACE' ? item.isCustom && !item.workspaceMemberId : Boolean(item.workspaceMemberId);
  const update = (/** @type {import('../../lib/navigation-layout.js').NavigationDraft} */ next) => {
    navigationDraftState.value = next;
  };
  const remove = async () => {
    try {
      await deleteNavigationItem(item.id);
      const current = navigationDraftState.value;
      if (current) navigationDraftState.value = { ...current, order: current.order.filter((id) => id !== item.id), hiddenIds: current.hiddenIds.filter((id) => id !== item.id) };
    } catch (error) {
      showErrorSnackbar(error);
    }
  };
  return html`<div class="navigation-edit-item" data-hidden=${isHidden ? 'true' : undefined} data-testid="navigation-edit-item">
    <span class="navigation-item-icon"><${Icon} name=${item.icon} size=${16} /></span>
    <span class="navigation-edit-item-label">${item.label}</span>
    <span class="navigation-edit-item-actions">
      ${draft.scope === 'WORKSPACE' && item.section !== 'SCHOOL_STRUCTURE'
        ? html`<select
            class="navigation-edit-section-select"
            aria-label=${t('navigationMobile.editor.moveToSection')}
            value=${draft.sections[item.id]}
            onChange=${(/** @type {any} */ event) => update(moveNavigationItemToSection(draft, item.id, event.currentTarget.value))}
          >
            ${sections.map((section) => html`<option key=${section} value=${section}>${t(`navigation.sections.${section}`)}</option>`)}
          </select>`
        : null}
      <${LightIconButton} icon="IconChevronUp" ariaLabel=${t('navigationMobile.editor.moveUp')} disabled=${isFirst} onClick=${() => update(moveNavigationItem(draft, item.id, -1))} />
      <${LightIconButton} icon="IconChevronDown" ariaLabel=${t('navigationMobile.editor.moveDown')} disabled=${isLast} onClick=${() => update(moveNavigationItem(draft, item.id, 1))} />
      <${LightIconButton}
        icon=${isHidden ? 'IconEyeOff' : 'IconEye'}
        ariaLabel=${isHidden ? t('navigationMobile.editor.show') : t('navigationMobile.editor.hide')}
        onClick=${() => update(toggleNavigationItemHidden(draft, item.id))}
      />
      ${canDelete ? html`<${LightIconButton} icon="IconTrash" ariaLabel=${t('navigationMobile.editor.remove')} onClick=${remove} />` : null}
    </span>
  </div>`;
}

/**
 * Itens de uma seção no modo de edição, na ordem do rascunho, com o botão de adicionar.
 * @param {{ section: string, sections: readonly string[] }} props
 */
export function EditableNavigationSectionItems({ section, sections }) {
  const draft = navigationDraftState.value;
  if (!draft) return null;
  const itemsById = new Map(allNavigationItemsState.value.map((item) => [item.id, item]));
  const sectionItems = draft.order
    .filter((id) => draft.sections[id] === section)
    .map((id) => itemsById.get(id))
    .filter((item) => item !== undefined);
  return html`<div class="navigation-section-items">
    ${sectionItems.map((item, index) => html`<${EditableNavigationItem}
      key=${item.id}
      item=${item}
      sections=${sections}
      isFirst=${index === 0}
      isLast=${index === sectionItems.length - 1}
    />`)}
    <${AddNavigationItemButton} section=${section} />
  </div>`;
}

/**
 * Adiciona uma view (das que o papel lê) ou um link na seção, no escopo do rascunho.
 * @param {{ section: string }} props
 */
function AddNavigationItemButton({ section }) {
  const anchorRef = useRef(/** @type {HTMLButtonElement | null} */ (null));
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [linkDraft, setLinkDraft] = useState(/** @type {{ name: string, link: string } | null} */ (null));
  const draft = navigationDraftState.value;
  if (!draft) return null;
  const scope = draft.scope;
  const normalizedSearch = searchText.trim().toLocaleLowerCase('pt-BR');
  const views = viewsState.value
    .map((view) => ({ view, objectMetadata: objectsById.value.get(view.objectMetadataId) }))
    .filter(({ view, objectMetadata }) => objectMetadata && !objectMetadata.isSystem && canReadObjectRecords(objectMetadata)
      // no padrão da escola só entram views da instituição
      && (scope === 'MEMBER' || view.visibility !== 'UNLISTED'))
    .map(({ view, objectMetadata }) => ({
      view,
      label: `${translateMetadataLabel(/** @type {any} */ (objectMetadata).labelPlural)} · ${translateMetadataLabel(view.name)}`,
      icon: view.icon ?? /** @type {any} */ (objectMetadata).icon ?? 'IconTable',
    }))
    .filter((entry) => !normalizedSearch || entry.label.toLocaleLowerCase('pt-BR').includes(normalizedSearch))
    .sort((first, second) => first.label.localeCompare(second.label, 'pt-BR'))
    .slice(0, 60);

  /** @param {Parameters<typeof createNavigationItem>[0]} input */
  const create = async (input) => {
    try {
      const item = await createNavigationItem({ ...input, scope, section });
      const current = navigationDraftState.value;
      if (item?.id && current) {
        navigationDraftState.value = { ...current, order: [...current.order, item.id], sections: { ...current.sections, [item.id]: section } };
      }
    } catch (error) {
      showErrorSnackbar(error);
    }
  };

  return html`
    <button ref=${anchorRef} type="button" class="navigation-edit-add" onClick=${() => setIsOpen(!isOpen)}>
      <${Icon} name="IconPlus" size=${14} />
      <span>${t('navigationMobile.editor.add')}</span>
    </button>
    <${Dropdown} anchorRef=${anchorRef} isOpen=${isOpen} onClose=${() => setIsOpen(false)} width=${280}>
      <${DropdownMenuSearchInput} value=${searchText} onChange=${setSearchText} placeholder=${t('navigationMobile.editor.searchViews')} />
      <${DropdownMenuSeparator} />
      <${DropdownMenuItems} hasMaxHeight=${true}>
        <${DropdownMenuSectionLabel} label=${t('navigationMobile.editor.views')} />
        ${views.map((entry) => html`<${MenuItem}
          key=${entry.view.id}
          icon=${entry.icon}
          text=${entry.label}
          onClick=${() => {
            setIsOpen(false);
            create({ type: 'VIEW', viewId: entry.view.id });
          }}
        />`)}
        <${DropdownMenuSeparator} />
        <${MenuItem} icon="IconLink" text=${t('navigationMobile.editor.link')} onClick=${() => {
          setIsOpen(false);
          setLinkDraft({ name: '', link: '' });
        }} />
      <//>
    <//>
    ${linkDraft
      ? html`<${Modal}
          title=${t('navigationMobile.editor.linkTitle')}
          confirmLabel=${t('navigationMobile.editor.add')}
          isConfirmDisabled=${!linkDraft.name.trim() || !linkDraft.link.trim()}
          onClose=${() => setLinkDraft(null)}
          onConfirm=${() => {
            const input = { type: /** @type {const} */ ('LINK'), name: linkDraft.name.trim(), link: linkDraft.link.trim() };
            setLinkDraft(null);
            create(input);
          }}
        >
          <label class="navigation-edit-field">
            <span>${t('navigationMobile.editor.linkName')}</span>
            <input class="text-input" value=${linkDraft.name} autofocus onInput=${(/** @type {any} */ event) => setLinkDraft({ ...linkDraft, name: event.currentTarget.value })} />
          </label>
          <label class="navigation-edit-field">
            <span>${t('navigationMobile.editor.linkAddress')}</span>
            <input class="text-input" value=${linkDraft.link} placeholder="https://" inputMode="url" onInput=${(/** @type {any} */ event) => setLinkDraft({ ...linkDraft, link: event.currentTarget.value })} />
          </label>
        <//>`
      : null}
  `;
}
